import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/auth/api';
import type {
  BillableLoad,
  CreditNote,
  DocumentSequence,
  Invoice,
  InvoicePayment,
} from '@breaker/shared-types';
import type {
  ConfigureSequenceBody,
  CreateCreditNoteBody,
  CreateInvoiceFromLoadsBody,
  RecordPaymentBody,
} from '@breaker/shared-schemas';

/**
 * Money Suite M2a — invoicing data layer.
 *
 * Every mutation here can be refused by a database rule (an issued invoice is
 * immutable, a load is already billed, the fleet's VAT status forbids the
 * document). Those arrive as 409s carrying a stable code and a message written
 * for the user, so screens surface `error.message` directly rather than
 * inventing their own copy — the explanation and the rule live together.
 */

const keys = {
  all: (fleetId: string) => ['invoices', fleetId] as const,
  list: (fleetId: string, filters: unknown) => ['invoices', fleetId, 'list', filters] as const,
  detail: (fleetId: string, id: string) => ['invoices', fleetId, 'detail', id] as const,
  billable: (fleetId: string, customerId?: string) =>
    ['invoices', fleetId, 'billable', customerId ?? ''] as const,
  settings: (fleetId: string) => ['invoices', fleetId, 'settings'] as const,
};

export interface InvoiceDetail {
  invoice: Invoice;
  payments: InvoicePayment[];
  creditNotes: CreditNote[];
}

export function useInvoices(
  fleetId: string | null,
  filters: { status?: Invoice['status']; customerId?: string } = {},
) {
  const query = {
    ...(filters.status ? { status: filters.status } : {}),
    ...(filters.customerId ? { customerId: filters.customerId } : {}),
  };
  return useQuery({
    queryKey: keys.list(fleetId ?? '', query),
    enabled: Boolean(fleetId),
    queryFn: () => api<{ invoices: Invoice[] }>(`/v1/fleets/${fleetId}/invoices`, { query }),
    select: (d) => d.invoices,
    staleTime: 15_000,
  });
}

export function useInvoice(fleetId: string | null, invoiceId: string | null) {
  return useQuery({
    queryKey: keys.detail(fleetId ?? '', invoiceId ?? ''),
    enabled: Boolean(fleetId && invoiceId),
    queryFn: () => api<InvoiceDetail>(`/v1/fleets/${fleetId}/invoices/${invoiceId}`),
    staleTime: 10_000,
  });
}

export function useBillableLoads(fleetId: string | null, customerId?: string) {
  return useQuery({
    queryKey: keys.billable(fleetId ?? '', customerId),
    enabled: Boolean(fleetId),
    queryFn: () =>
      api<{ loads: BillableLoad[] }>(`/v1/fleets/${fleetId}/billable-loads`, {
        query: customerId ? { customerId } : {},
      }),
    select: (d) => d.loads,
    staleTime: 10_000,
  });
}

export function useInvoiceSettings(fleetId: string | null) {
  return useQuery({
    queryKey: keys.settings(fleetId ?? ''),
    enabled: Boolean(fleetId),
    queryFn: () =>
      api<{ sequences: DocumentSequence[] }>(`/v1/fleets/${fleetId}/invoice-settings`),
    select: (d) => d.sequences,
    staleTime: 60_000,
  });
}

/**
 * Issuing and billing both change what the loads list may offer, so these
 * invalidate the loads cache as well as the invoice caches. Missing that is
 * how a just-billed load stays in the picker and gets billed twice — which the
 * database would refuse, but as a confusing error rather than a clean screen.
 */
function useInvoiceMutation<TBody, TResult>(
  fleetId: string | null,
  fn: (body: TBody) => Promise<TResult>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.all(fleetId ?? '') });
      qc.invalidateQueries({ queryKey: ['loads', fleetId ?? ''] });
      qc.invalidateQueries({ queryKey: ['stats', fleetId ?? ''] });
    },
  });
}

export function useCreateInvoiceFromLoads(fleetId: string | null) {
  return useInvoiceMutation(fleetId, (body: CreateInvoiceFromLoadsBody) =>
    api<{ invoice: Invoice }>(`/v1/fleets/${fleetId}/invoices/from-loads`, {
      method: 'POST',
      body,
    }),
  );
}

export function useIssueInvoice(fleetId: string | null) {
  return useInvoiceMutation(fleetId, (vars: { invoiceId: string; issuedAt?: string }) =>
    api<{ invoice: Invoice }>(`/v1/fleets/${fleetId}/invoices/${vars.invoiceId}/issue`, {
      method: 'POST',
      body: vars.issuedAt ? { issuedAt: vars.issuedAt } : {},
    }),
  );
}

export function useVoidInvoice(fleetId: string | null) {
  return useInvoiceMutation(fleetId, (invoiceId: string) =>
    api<{ invoice: Invoice }>(`/v1/fleets/${fleetId}/invoices/${invoiceId}/void`, {
      method: 'POST',
    }),
  );
}

export function useDeleteDraft(fleetId: string | null) {
  return useInvoiceMutation(fleetId, (invoiceId: string) =>
    api<void>(`/v1/fleets/${fleetId}/invoices/${invoiceId}`, { method: 'DELETE' }),
  );
}

export function useRecordPayment(fleetId: string | null) {
  return useInvoiceMutation(
    fleetId,
    (vars: { invoiceId: string } & RecordPaymentBody) =>
      api<{ payment: InvoicePayment }>(
        `/v1/fleets/${fleetId}/invoices/${vars.invoiceId}/payments`,
        { method: 'POST', body: vars },
      ),
  );
}

export function useRemovePayment(fleetId: string | null) {
  return useInvoiceMutation(fleetId, (vars: { invoiceId: string; paymentId: string }) =>
    api<void>(
      `/v1/fleets/${fleetId}/invoices/${vars.invoiceId}/payments/${vars.paymentId}`,
      { method: 'DELETE' },
    ),
  );
}

export function useCreateCreditNote(fleetId: string | null) {
  return useInvoiceMutation(
    fleetId,
    (vars: { invoiceId: string } & CreateCreditNoteBody) =>
      api<{ creditNote: CreditNote }>(
        `/v1/fleets/${fleetId}/invoices/${vars.invoiceId}/credit-notes`,
        { method: 'POST', body: vars },
      ),
  );
}

export function useIssueCreditNote(fleetId: string | null) {
  return useInvoiceMutation(fleetId, (creditNoteId: string) =>
    api<{ creditNote: CreditNote }>(
      `/v1/fleets/${fleetId}/credit-notes/${creditNoteId}/issue`,
      { method: 'POST' },
    ),
  );
}

export function useConfigureSequence(fleetId: string | null) {
  return useInvoiceMutation(
    fleetId,
    (vars: { kind: 'invoice' | 'credit_note' } & ConfigureSequenceBody) =>
      api<{ sequence: DocumentSequence }>(
        `/v1/fleets/${fleetId}/invoice-settings/${vars.kind}`,
        { method: 'PUT', body: vars },
      ),
  );
}
